import { createAction, createReducer, createSelector } from '@reduxjs/toolkit';
import Actions from 'Actions';
import { findKey, keys, omit } from 'lodash';
import type { StorytellerState } from 'store/StorytellerReduxState';

// Breakpoints for Window Width
// Keep this in sync with lib/variables/layout.scss.
export const WINDOW_SIZE_BREAK_XLARGE = 1400;
export const WINDOW_SIZE_BREAK_LARGE = 1024;
export const WINDOW_SIZE_BREAK_MEDIUM = 768;

export interface WindowSizeState {
  windowSizeClasses: {
    xlarge: boolean;
    large: boolean;
    medium: boolean;
    small: boolean;
  };
}

const computeWindowSizeClasses = (windowSize: number) => ({
  xlarge: windowSize >= WINDOW_SIZE_BREAK_XLARGE,
  large: windowSize < WINDOW_SIZE_BREAK_XLARGE && windowSize >= WINDOW_SIZE_BREAK_LARGE,
  medium: windowSize < WINDOW_SIZE_BREAK_LARGE && windowSize >= WINDOW_SIZE_BREAK_MEDIUM,
  small: windowSize < WINDOW_SIZE_BREAK_MEDIUM,
});

// Initial state
const initialState: WindowSizeState = {
  windowSizeClasses: computeWindowSizeClasses(window.innerWidth),
};

// Action creator
export const windowResized = createAction(Actions.WINDOW_RESIZED, (windowWidth: number) => ({
  payload: windowWidth,
}));

// Reducer
export const windowSizeReducer = createReducer(initialState, (builder) => {
  builder.addCase(windowResized, (state, action) => {
    state.windowSizeClasses = computeWindowSizeClasses(action.payload);
  });
});

export const selectWindowSizeClassesSlice = (state: StorytellerState) => state.windowSize;

export const selectWindowSizeClasses = createSelector(
  selectWindowSizeClassesSlice,
  (slice) => slice.windowSizeClasses,
);

export const selectWindowSizeClass = createSelector(
  selectWindowSizeClasses,
  (windowSizeClasses) => findKey(windowSizeClasses, (sizeClass) => !!sizeClass)!,
);

export const selectUnusedWindowSizeClasses = createSelector(
  [selectWindowSizeClass, selectWindowSizeClasses],
  (windowSizeClass, windowSizeClasses) => keys(omit(windowSizeClasses, windowSizeClass)),
);
